import type { UseQueryResult } from "@tanstack/react-query";
import { AlertTriangleIcon, LockIcon, RotateCwIcon } from "lucide-react";
import type { ReactNode } from "react";

import { errorMessage, isApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { EmptyState } from "./empty-state";

interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({ error, onRetry, className }: ErrorStateProps) {
  const notFound = isApiError(error) && error.status === 404;
  // Refusals (e.g. a learner whose access window ended) won't change on retry; the server says why.
  const denied = isApiError(error) && error.status === 403;
  const title = notFound
    ? "Not found"
    : denied
      ? isApiError(error) && error.code === "access_ended"
        ? "Your access has ended"
        : "No access"
      : "Couldn't load this";
  return (
    <EmptyState
      className={className}
      icon={denied ? LockIcon : AlertTriangleIcon}
      title={title}
      description={notFound ? "It may have been removed, or you may not have access." : errorMessage(error)}
      action={
        onRetry && !notFound && !denied ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            <RotateCwIcon /> Try again
          </Button>
        ) : undefined
      }
    />
  );
}

/** Generic page-level loading placeholder. */
export function PageSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-6", className)} aria-busy="true" aria-label="Loading">
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-8 w-72" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-72 rounded-xl" />
    </div>
  );
}

/** Page skeleton for screens rendered outside any layout (auth checks, first route load). */
export function FullPageSkeleton() {
  return (
    <div className="mx-auto max-w-6xl p-6 sm:p-10">
      <PageSkeleton />
    </div>
  );
}

interface QueryStateProps<T> {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
  /** Shown while the first load is in flight (defaults to PageSkeleton). */
  loading?: ReactNode;
  className?: string;
}

/**
 * Renders loading / error / data states of a TanStack query uniformly:
 *
 *   <QueryState query={useCourse(id)}>{(course) => <CourseView course={course} />}</QueryState>
 */
export function QueryState<T>({ query, children, loading, className }: QueryStateProps<T>) {
  if (query.isPending) return <>{loading ?? <PageSkeleton className={className} />}</>;
  if (query.isError)
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} className={className} />;
  return <>{children(query.data)}</>;
}
